import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';

import { useLanguage } from '@/contexts';
import { Clinic, getClinic } from '@/data/Clinics';
import { DentistHour, getDentistHoursByDentistId } from '@/data/DentistHours';
import { Dentist, getAvailableDentistsByClinicId } from '@/data/Dentists';
import { HmoProvider, getHmoProvidersByDentistId } from '@/data/HmoProviders';
import {
  Patient,
  createPatient,
  getPatient,
  updatePatient
} from '@/data/Patients';
import {
  Request,
  createRequest,
  getDentistFutureAppointmentsAndRequests
} from '@/data/Requests';
import {
  Service,
  getDefaultServices,
  getServicesByDentistId
} from '@/data/Services';
import { useToast } from '@/hooks';
import {
  DEFAULT_DURATION_IN_MINUTES,
  MAXIMUM_DURATION_IN_MINUTES,
  OVERLAP_TIME_REDUCTION_IN_MINUTES,
  PURPOSE_JOIN_SEPARATOR,
  REQUEST_STATUS_OPTIONS,
  SCHEDULE_FLOW_STEPS,
  SELECT_MODAL_OTHERS
} from '@/utils/constants';
import {
  collapseDentistHours,
  conflictsWithRequests,
  findDayHours,
  getAvailableDates,
  getOpenTimeslots,
  isWithinWorkingHours
} from '@/utils/helpers/appointmentSlots';
import { formatDentistName } from '@/utils/helpers/formatDentistName';
import { formatMobileNumber } from '@/utils/helpers/formatMobileNumber';
import { formatName } from '@/utils/helpers/formatName';
import { generateUid, isValidUid } from '@/utils/helpers/uid';
import { supabase } from '@/utils/supabase';
import { Gender } from '@/utils/types';
import {
  UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query';
import moment from 'moment';
import { usePostHog } from 'posthog-js/react';
import { useParams } from 'react-router';

interface ScheduleFlowContextProps {
  loading: boolean;
  clinic: Clinic | null;
  dentists: Dentist[];
  selectedDentist: Dentist | null;
  setSelectedDentist: React.Dispatch<React.SetStateAction<Dentist | null>>;
  availableDates: Date[];
  setAvailableDates: React.Dispatch<React.SetStateAction<Date[]>>;
  availableTimeslots: string[];
  setAvailableTimeslots: React.Dispatch<React.SetStateAction<string[]>>;
  selectedDate: Date | null;
  setSelectedDate: React.Dispatch<React.SetStateAction<Date | null>>;
  selectedTime: string | null;
  setSelectedTime: React.Dispatch<React.SetStateAction<string | null>>;
  purpose: string | null;
  setPurpose: React.Dispatch<React.SetStateAction<string | null>>;
  othersPurpose: string | null;
  setOthersPurpose: React.Dispatch<React.SetStateAction<string | null>>;
  currentStep: string;
  setCurrentStep: React.Dispatch<React.SetStateAction<string>>;
  loadAvailableDates: () => Promise<void>;
  patient: Patient | null;
  patientId: string | null;
  firstName: string | null;
  lastName: string | null;
  mobileNumber: string | null;
  gender: Gender | null;
  birthday: string | null;
  hmoProviderName: string | null;
  setHmoProviderName: React.Dispatch<React.SetStateAction<string | null>>;
  hmoProviders: HmoProvider[];
  setFirstName: React.Dispatch<React.SetStateAction<string | null>>;
  setLastName: React.Dispatch<React.SetStateAction<string | null>>;
  setMobileNumber: React.Dispatch<React.SetStateAction<string | null>>;
  setGender: React.Dispatch<React.SetStateAction<Gender | null>>;
  setBirthday: React.Dispatch<React.SetStateAction<string | null>>;
  durationInMinutes: number;
  services: Service[];
  sendOtp: () => Promise<void>;
  resendOtp: () => Promise<void>;
  verifyOtp: (otp: string) => Promise<void>;
  request: Request | null;
  confirmRequest: () => Promise<void>;
  reset: () => void;
  authenticatedPatientId: string | null;
}

const ScheduleFlowContext = createContext<ScheduleFlowContextProps | undefined>(
  undefined
);

interface ScheduleFlowProviderProps {
  children: React.ReactNode;
}

export const ScheduleFlowProvider: React.FC<ScheduleFlowProviderProps> = ({
  children
}) => {
  const posthog = usePostHog();
  const queryClient = useQueryClient();
  const displayToast = useToast();
  const { setLanguageFromClinic } = useLanguage();
  const { id: clinicId } = useParams<{ id: string }>();

  const [selectedDentist, setSelectedDentist] = useState<Dentist | null>(null);
  const [availableDates, setAvailableDates] = useState<Date[]>([]);
  const [availableTimeslots, setAvailableTimeslots] = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [purpose, setPurpose] = useState<string | null>(null);
  const [othersPurpose, setOthersPurpose] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState<string>(
    SCHEDULE_FLOW_STEPS.SELECT_DENTIST
  );
  const [maxStepReached, setMaxStepReached] = useState<string>(
    SCHEDULE_FLOW_STEPS.SELECT_DENTIST
  );
  const [patientId, setPatientId] = useState<string | null>(null);
  const [firstName, setFirstName] = useState<string | null>(null);
  const [lastName, setLastName] = useState<string | null>(null);
  const [mobileNumber, setMobileNumber] = useState<string | null>(null);
  const [gender, setGender] = useState<Gender | null>(null);
  const [birthday, setBirthday] = useState<string | null>(null);
  const [hmoProviderName, setHmoProviderName] = useState<string | null>(null);
  const [authenticated, setAuthenticated] = useState<boolean>(false);
  const [authenticatedPatientId, setAuthenticatedPatientId] = useState<
    string | null
  >(null);
  const [authenticatedMobileNumber, setAuthenticatedMobileNumber] = useState<
    string | null
  >(null);

  const createRequestMutation = useMutation({
    mutationFn: createRequest,
    retry: false,
    onSuccess: () => {
      setCurrentStep(SCHEDULE_FLOW_STEPS.THANK_YOU);
    },
    onError: (error) => {
      displayToast({
        message: `Error creating request: ${error instanceof Error ? error.message : 'Unknown error'}. Please contact support.`,
        position: 'bottom',
        positionAnchor: 'nextButton'
      });
    }
  });

  const createPatientMutation = useMutation({
    mutationFn: createPatient,
    retry: false,
    onError: (error) => {
      displayToast({
        message: `Error creating patient: ${error instanceof Error ? error.message : 'Unknown error'}. Please contact support.`,
        position: 'bottom',
        positionAnchor: 'nextButton'
      });
    }
  });

  const updatePatientMutation = useMutation({
    mutationFn: updatePatient,
    retry: false,
    onError: (error) => {
      displayToast({
        message: `Error updating patient: ${error instanceof Error ? error.message : 'Unknown error'}. Please contact support.`,
        position: 'bottom',
        positionAnchor: 'nextButton'
      });
    }
  });

  const sendOtpMutation = useMutation({
    mutationFn: async (payload: { phoneNumber: string }) => {
      if (!selectedDentist?.patient_verification_enabled) {
        if (!patientId) {
          const uid = generateUid();
          await createPatientMutation.mutateAsync({
            ...patient,
            id: uid,
            user_id: authenticatedPatientId,
            dentist_id: selectedDentist?.id
          });
          setPatientId(uid);
        }
        setCurrentStep(SCHEDULE_FLOW_STEPS.CONFIRMATION);
        return;
      }

      if (
        payload.phoneNumber &&
        authenticated &&
        `63${payload.phoneNumber}` === authenticatedMobileNumber &&
        authenticatedPatientId
      ) {
        const existingPatient = await getPatient(authenticatedPatientId);
        if (existingPatient) {
          await updatePatientMutation.mutateAsync({
            patientId: authenticatedPatientId,
            patient
          });
        } else {
          await createPatientMutation.mutateAsync({
            ...patient,
            id: authenticatedPatientId,
            is_user: true,
            user_id: authenticatedPatientId,
            dentist_id: selectedDentist?.id
          });
        }

        if (!patientId) {
          const uid = generateUid();
          await createPatientMutation.mutateAsync({
            ...patient,
            id: uid,
            user_id: authenticatedPatientId,
            dentist_id: selectedDentist?.id
          });
          setPatientId(uid);
        }
        setCurrentStep(SCHEDULE_FLOW_STEPS.CONFIRMATION);
      } else {
        const { error } = await supabase.functions.invoke('otp', {
          body: {
            operation: 'send',
            payload: {
              phone_number: `63${payload.phoneNumber}`
            }
          }
        });

        if (error) {
          throw new Error(`Error sending OTP: ${error.message}`);
        } else {
          setCurrentStep(SCHEDULE_FLOW_STEPS.VERIFICATION);
        }
      }
    },
    retry: false,
    onError: () => {
      displayToast({
        message: `Failed to send OTP. Please try again.`,
        position: 'bottom',
        positionAnchor: 'nextButton'
      });
    }
  });

  const resendOtpMutation = useMutation({
    mutationFn: async (phoneNumber: string) => {
      const { error } = await supabase.functions.invoke('otp', {
        body: {
          operation: 'resend',
          payload: {
            phone_number: `63${phoneNumber}`
          }
        }
      });

      if (error) {
        throw new Error(`Error resending OTP: ${error.message}`);
      }
    },
    retry: false,
    onError: () => {
      displayToast({
        message: `Failed to resend OTP. Please try again.`,
        position: 'bottom',
        positionAnchor: 'nextButton'
      });
    }
  });

  const verifyOtpMutation = useMutation({
    mutationFn: async (payload: { phoneNumber: string; otp: string }) => {
      const {
        data: { session },
        error
      } = await supabase.auth.verifyOtp({
        phone: `63${payload.phoneNumber}`,
        token: payload.otp,
        type: 'sms'
      });

      if (error) {
        throw new Error(`Error verifying OTP: ${error.message}`);
      }

      if (session && session.user.id && session.user.phone) {
        const existingPatient = await getPatient(session.user.id);
        if (existingPatient) {
          await updatePatientMutation.mutateAsync({
            patientId: session.user.id,
            patient
          });
        } else {
          await createPatientMutation.mutateAsync({
            ...patient,
            id: session.user.id,
            is_user: true,
            user_id: session.user.id,
            dentist_id: selectedDentist?.id
          });
        }

        if (!patientId) {
          const uid = generateUid();
          await createPatientMutation.mutateAsync({
            ...patient,
            id: uid,
            user_id: session.user.id,
            dentist_id: selectedDentist?.id
          });
          setPatientId(uid);
        }
        setCurrentStep(SCHEDULE_FLOW_STEPS.CONFIRMATION);
      }
    },
    retry: false,
    onError: (error) => {
      displayToast({
        message: `${error instanceof Error ? error.message : 'Unknown error.'}`,
        position: 'bottom',
        positionAnchor: 'nextButton'
      });
    }
  });

  const loadAvailableDatesMutation = useMutation({
    mutationFn: async (
      payload: { forceRefresh: boolean } = { forceRefresh: false }
    ) => {
      if (!selectedDentist) {
        displayToast({
          message: `No dentist selected. Please select a dentist and try again.`,
          position: 'bottom',
          positionAnchor: 'nextButton'
        });
        setCurrentStep(SCHEDULE_FLOW_STEPS.SELECT_DENTIST);
        return;
      }

      if (!dentistHours || !requests) {
        displayToast({
          message: `Loading dentist schedule, please wait and try again...`,
          position: 'bottom',
          positionAnchor: 'nextButton'
        });
        setCurrentStep(SCHEDULE_FLOW_STEPS.SELECT_PURPOSE);
        return;
      }

      if (availableDates.length > 0 && !payload.forceRefresh) {
        setCurrentStep(SCHEDULE_FLOW_STEPS.SELECT_DATE);
        return;
      }

      return getAvailableDates({
        dentistHours,
        requests,
        durationInMinutes
      });
    },
    onSuccess: (dates?: Date[]) => {
      if (!dates) {
        return;
      }

      if (dates.length > 0) {
        setAvailableDates(dates);
        setSelectedDate(dates[0]);
        setCurrentStep(SCHEDULE_FLOW_STEPS.SELECT_DATE);
      } else {
        displayToast({
          message: `${formatDentistName({
            dentist: selectedDentist!,
            returnDentistFullName: true
          })} is unavailable. ${
            (dentists ?? []).length > 1
              ? 'Please choose another dentist or contact us at '
              : 'For assistance, please contact us at '
          }${formatMobileNumber(clinic?.mobile_number)}.`,
          duration: 8000,
          position: 'bottom',
          positionAnchor: 'nextButton'
        });
        setCurrentStep(SCHEDULE_FLOW_STEPS.SELECT_DENTIST);
      }
    },
    onError: (error) => {
      displayToast({
        message: `Error fetching dentist schedule: ${error instanceof Error ? error.message : 'Unknown error'}. Please contact support.`,
        position: 'bottom',
        positionAnchor: 'nextButton'
      });
    }
  });

  const confirmRequestMutation = useMutation({
    mutationFn: async () => {
      if (!selectedDentist?.id) {
        throw new Error('No dentist selected');
      }

      const [existingRequests, rawHours] = await Promise.all([
        getDentistFutureAppointmentsAndRequests(selectedDentist.id!),
        getDentistHoursByDentistId(selectedDentist.id!)
      ]);
      const hours = collapseDentistHours(rawHours);
      queryClient.setQueryData(['dentistHours', selectedDentist.id], hours);
      queryClient.setQueryData(
        ['dentistRequests', selectedDentist.id],
        existingRequests
      );

      const date = request.date;
      const startTime = request.start_time;
      const endTime = request.end_time;
      const outsideWorkingHours =
        !date ||
        !startTime ||
        !endTime ||
        !isWithinWorkingHours(
          date,
          startTime,
          endTime,
          findDayHours(hours, date)
        );
      const conflicting =
        !!date &&
        !!startTime &&
        !!endTime &&
        conflictsWithRequests(date, startTime, endTime, existingRequests);

      if (outsideWorkingHours || conflicting) {
        displayToast({
          message: outsideWorkingHours
            ? "That time is outside this dentist's hours. Please choose a different time."
            : 'The timeslot you selected has just been booked by another patient. Please choose a different timeslot.',
          duration: 8000,
          position: 'bottom',
          positionAnchor: 'nextButton'
        });
        const dates = getAvailableDates({
          dentistHours: hours,
          requests: existingRequests,
          durationInMinutes
        });
        setAvailableDates(dates);
        const selectedStillOpen =
          !!date &&
          dates.some((availableDate) =>
            moment(availableDate).isSame(date, 'day')
          );
        setSelectedDate(
          selectedStillOpen
            ? moment(date, 'YYYY-MM-DD').toDate()
            : (dates[0] ?? null)
        );
        setSelectedTime(null);
        setCurrentStep(
          dates.length > 0
            ? SCHEDULE_FLOW_STEPS.SELECT_DATE
            : SCHEDULE_FLOW_STEPS.SELECT_DENTIST
        );
        return;
      }

      return await createRequestMutation.mutateAsync(request);
    },
    retry: false,
    onSuccess: (created) => {
      if (created) {
        setCurrentStep(SCHEDULE_FLOW_STEPS.THANK_YOU);
      }
    },
    onError: (error) => {
      displayToast({
        message: `Error confirming request: ${error instanceof Error ? error.message : 'Unknown error'}. Please contact support.`,

        position: 'bottom',
        positionAnchor: 'nextButton'
      });
    }
  });

  const {
    data: clinic,
    isLoading: loadingClinic,
    isFetched: loadedClinic
  }: UseQueryResult<Clinic, Error> = useQuery({
    queryKey: ['clinic', clinicId],
    queryFn: async () => {
      if (!clinicId || !isValidUid(clinicId)) {
        displayToast({
          message:
            'Invalid clinic ID. Please double check the URL and try again.',
          position: 'bottom',
          positionAnchor: 'nextButton'
        });
        return null;
      }
      return getClinic(clinicId);
    },
    enabled: !!clinicId && isValidUid(clinicId)
  });

  const {
    data: dentists,
    isLoading: loadingDentists,
    isFetched: loadedDentists
  }: UseQueryResult<Dentist[], Error> = useQuery({
    queryKey: ['dentists', clinicId],
    queryFn: async () => {
      if (!clinicId || !isValidUid(clinicId)) {
        return [] as Dentist[];
      }
      try {
        const dentistsData = await getAvailableDentistsByClinicId(clinicId);
        return [...dentistsData].sort((a, b) =>
          a.first_name.localeCompare(b.first_name)
        );
      } catch (error) {
        displayToast({
          message: `Error fetching dentists: ${error instanceof Error ? error.message : 'Unknown error'}. Please contact support.`,
          position: 'bottom',
          positionAnchor: 'nextButton'
        });
        return [] as Dentist[];
      }
    },
    enabled: !!clinicId && isValidUid(clinicId)
  });

  const {
    data: dentistHours,
    isLoading: loadingDentistHours,
    isFetched: loadedDentistHours
  }: UseQueryResult<DentistHour[], Error> = useQuery({
    queryKey: ['dentistHours', selectedDentist?.id],
    queryFn: async () => {
      if (!selectedDentist?.id) return [];
      try {
        const result = await getDentistHoursByDentistId(selectedDentist.id);
        return collapseDentistHours(result);
      } catch (error) {
        displayToast({
          message: `Error fetching dentist hours: ${error instanceof Error ? error.message : 'Unknown error'}. Please contact support.`,
          position: 'bottom',
          positionAnchor: 'nextButton'
        });
        setCurrentStep(SCHEDULE_FLOW_STEPS.SELECT_PURPOSE);
        throw error;
      }
    },
    retry: false,
    enabled: !!selectedDentist?.id && isValidUid(selectedDentist.id)
  });

  const {
    data: requests,
    isLoading: loadingRequests,
    isFetched: loadedRequests
  }: UseQueryResult<Request[], Error> = useQuery({
    queryKey: ['dentistRequests', selectedDentist?.id],
    queryFn: async () => {
      if (!selectedDentist?.id) return [];
      try {
        const result = await getDentistFutureAppointmentsAndRequests(
          selectedDentist.id
        );
        return result;
      } catch (error) {
        displayToast({
          message: `Error fetching dentist appointments: ${error instanceof Error ? error.message : 'Unknown error'}. Please contact support.`,
          position: 'bottom',
          positionAnchor: 'nextButton'
        });
        setCurrentStep(SCHEDULE_FLOW_STEPS.SELECT_DENTIST);
        throw error;
      }
    },
    retry: false,
    enabled: !!selectedDentist?.id && isValidUid(selectedDentist.id)
  });

  const {
    data: services,
    isLoading: loadingServices,
    isFetched: loadedServices
  }: UseQueryResult<Service[], Error> = useQuery({
    queryKey: ['services', selectedDentist?.id],
    queryFn: async () => {
      if (!selectedDentist?.id) return [] as Service[];
      try {
        const options = await getServicesByDentistId(selectedDentist.id);
        if (options.length > 0) {
          return options;
        } else {
          const defaultOptions = await getDefaultServices();
          return defaultOptions;
        }
      } catch (error) {
        displayToast({
          message: `Error fetching purpose options: ${error instanceof Error ? error.message : 'Unknown error'}. Please contact support.`,
          position: 'bottom',
          positionAnchor: 'nextButton'
        });
        setCurrentStep(SCHEDULE_FLOW_STEPS.SELECT_DENTIST);
        return [] as Service[];
      }
    },
    enabled: !!selectedDentist?.id && isValidUid(selectedDentist.id)
  });

  const {
    data: hmoProviders,
    isLoading: loadingHmoProviders,
    isFetched: loadedHmoProviders
  }: UseQueryResult<HmoProvider[], Error> = useQuery({
    queryKey: ['hmoProviders', selectedDentist?.id],
    queryFn: async () => {
      if (!selectedDentist?.id) return [] as HmoProvider[];
      try {
        const options = await getHmoProvidersByDentistId(selectedDentist.id);
        return options;
      } catch (error) {
        displayToast({
          message: `Error fetching HMO providers: ${error instanceof Error ? error.message : 'Unknown error'}. Please contact support.`,
          position: 'bottom',
          positionAnchor: 'nextButton'
        });
        return [] as HmoProvider[];
      }
    },
    enabled: !!selectedDentist?.id && isValidUid(selectedDentist.id)
  });

  const durationInMinutes: number = useMemo(() => {
    if (purpose && services && services.length > 0) {
      const calculatedDuration = purpose
        .split(PURPOSE_JOIN_SEPARATOR)
        .sort((a, b) => {
          const durationA =
            services.find((option) => option.name === a)?.duration ||
            DEFAULT_DURATION_IN_MINUTES;
          const durationB =
            services.find((option) => option.name === b)?.duration ||
            DEFAULT_DURATION_IN_MINUTES;
          return durationB - durationA;
        })
        .reduce(
          (total, purpose, index, purposes) =>
            total +
            (services.find((option) => option.name === purpose)?.duration ||
              DEFAULT_DURATION_IN_MINUTES) -
            (purposes.length > 1 && index !== 0
              ? OVERLAP_TIME_REDUCTION_IN_MINUTES
              : 0),
          0
        );

      return Math.min(calculatedDuration, MAXIMUM_DURATION_IN_MINUTES);
    }

    return DEFAULT_DURATION_IN_MINUTES;
  }, [services, purpose]);

  const patient: Patient = useMemo(
    () => ({
      first_name: formatName(firstName),
      last_name: formatName(lastName),
      mobile_number: mobileNumber,
      gender,
      birthday
    }),
    [firstName, lastName, mobileNumber, gender, birthday]
  );

  const request: Request = useMemo(
    () => ({
      date: selectedDate ? moment(selectedDate).format('YYYY-MM-DD') : null,
      start_time: selectedTime,
      end_time: selectedTime
        ? moment(selectedTime, 'HH:mm')
            .add(durationInMinutes, 'minutes')
            .format('HH:mm')
        : null,
      dentist_id: selectedDentist?.id!,
      status: REQUEST_STATUS_OPTIONS.PENDING,
      purpose:
        purpose === SELECT_MODAL_OTHERS &&
        othersPurpose &&
        othersPurpose.trim() !== ''
          ? `${purpose}: ${othersPurpose}`
          : purpose,
      patient_id: patientId,
      hmo_provider: hmoProviderName
    }),
    [
      selectedDate,
      selectedTime,
      durationInMinutes,
      selectedDentist,
      purpose,
      othersPurpose,
      patientId,
      hmoProviderName
    ]
  );

  // Set language based on clinic data
  useEffect(() => {
    if (clinic && clinic.language) {
      setLanguageFromClinic(clinic.language);
    }
  }, [clinic, setLanguageFromClinic]);

  const loading =
    loadingClinic ||
    !loadedClinic ||
    loadingDentists ||
    !loadedDentists ||
    (currentStep === SCHEDULE_FLOW_STEPS.SELECT_PURPOSE &&
      (loadingDentistHours || !loadedDentistHours)) ||
    (currentStep === SCHEDULE_FLOW_STEPS.SELECT_PURPOSE &&
      (loadingRequests || !loadedRequests)) ||
    (currentStep === SCHEDULE_FLOW_STEPS.SELECT_PURPOSE &&
      (loadingServices || !loadedServices)) ||
    (currentStep === SCHEDULE_FLOW_STEPS.SELECT_PURPOSE &&
      (loadingHmoProviders || !loadedHmoProviders)) ||
    createRequestMutation.isPending ||
    createPatientMutation.isPending ||
    updatePatientMutation.isPending ||
    sendOtpMutation.isPending ||
    resendOtpMutation.isPending ||
    verifyOtpMutation.isPending ||
    loadAvailableDatesMutation.isPending ||
    confirmRequestMutation.isPending;

  const getTimeslotsForDate = useCallback(
    (date: Date) => {
      if (!selectedDentist || !dentistHours || !requests) {
        return [];
      }

      return getOpenTimeslots({
        date,
        dentistHours,
        requests,
        durationInMinutes
      });
    },
    [selectedDentist, dentistHours, requests, durationInMinutes]
  );

  const loadAvailableDates = async () => {
    await loadAvailableDatesMutation.mutateAsync({ forceRefresh: false });
  };

  const sendOtp = async () => {
    await sendOtpMutation.mutateAsync({ phoneNumber: mobileNumber! });
  };

  const resendOtp = async () => {
    await resendOtpMutation.mutateAsync(mobileNumber!);
  };

  const verifyOtp = async (otp: string) => {
    await verifyOtpMutation
      .mutateAsync({ phoneNumber: mobileNumber!, otp })
      .catch(() => {}); // Prevents sending error to Sentry and PostHog
  };

  const confirmRequest = async () => {
    await confirmRequestMutation.mutateAsync();
  };

  const reset = useCallback(async () => {
    await supabase.auth.signOut();
    setCurrentStep(SCHEDULE_FLOW_STEPS.SELECT_DENTIST);
    setSelectedDentist(dentists && dentists.length > 0 ? dentists[0] : null);
    setAvailableDates([]);
    setAvailableTimeslots([]);
    setPatientId(null);
    setFirstName(null);
    setLastName(null);
    setMobileNumber(null);
    setGender(null);
    setBirthday(null);
    setAuthenticatedMobileNumber(null);
    setAuthenticatedPatientId(null);
    setAuthenticated(false);
    setSelectedDate(null);
    setPurpose(null);
  }, [dentists]);

  useEffect(() => {
    const fetchUser = async () => {
      const {
        data: { user }
      } = await supabase.auth.getUser();

      if (user) {
        const patient = await getPatient(user.id);
        setAuthenticated(true);
        setAuthenticatedMobileNumber(user.phone!);
        setAuthenticatedPatientId(user.id);
        if (patient) {
          setFirstName(patient.first_name);
          setLastName(patient.last_name);
          setMobileNumber(patient.mobile_number);
          setGender(patient.gender);
          setBirthday(patient.birthday);
        }
      }
    };

    fetchUser();
  }, []);

  useEffect(() => {
    if (selectedDate) {
      const timeslots = getTimeslotsForDate(selectedDate);
      setAvailableTimeslots(timeslots);
      setSelectedTime(timeslots[0]);
    } else {
      setAvailableTimeslots([]);
    }
  }, [selectedDate, getTimeslotsForDate]);

  useEffect(() => {
    if (currentStep === maxStepReached) {
      return;
    }

    if (
      Object.values(SCHEDULE_FLOW_STEPS).indexOf(currentStep) >
      Object.values(SCHEDULE_FLOW_STEPS).indexOf(maxStepReached)
    ) {
      setMaxStepReached(currentStep);
    }
  }, [currentStep, maxStepReached]);

  useEffect(() => {
    if (posthog && maxStepReached) {
      posthog.capture('step', {
        step: maxStepReached
      });
    }
  }, [maxStepReached, posthog]);

  useEffect(() => {
    /**
     * Sets the first available dentist as the default selection
     * if no dentist is currently selected and dentists are available
     */
    if (dentists && dentists.length > 0 && !selectedDentist) {
      setSelectedDentist(dentists[0]);
    }
  }, [dentists]);

  useEffect(() => {
    setHmoProviderName(null);
  }, [selectedDentist?.id]);

  useEffect(() => {
    /**
     * Removes services that are not available for the selected dentist
     */
    if (purpose && services && services.length > 0) {
      const purposeArray = purpose.split(PURPOSE_JOIN_SEPARATOR);
      const availableServices = services.map((service) => service.name);
      const filteredPurposes = purposeArray.filter((p) =>
        availableServices.includes(p)
      );

      if (filteredPurposes.length !== purposeArray.length) {
        if (filteredPurposes.length > 0) {
          setPurpose(filteredPurposes.join(PURPOSE_JOIN_SEPARATOR));
        } else {
          setPurpose(null);
        }
        displayToast({
          message:
            'Some procedures you selected are not available for this dentist and have been removed.',
          duration: 5000,
          position: 'bottom',
          positionAnchor: 'nextButton'
        });
      }
    }
  }, [services]);

  return (
    <ScheduleFlowContext.Provider
      value={{
        loading,
        clinic: clinic ?? null,
        dentists: dentists ?? [],
        selectedDentist,
        setSelectedDentist,
        availableDates,
        setAvailableDates,
        availableTimeslots,
        setAvailableTimeslots,
        selectedDate,
        setSelectedDate,
        selectedTime,
        setSelectedTime,
        purpose,
        setPurpose,
        othersPurpose,
        setOthersPurpose,
        currentStep,
        setCurrentStep,
        loadAvailableDates,
        patient,
        patientId,
        firstName,
        lastName,
        mobileNumber,
        gender,
        birthday,
        hmoProviderName,
        setHmoProviderName,
        hmoProviders: hmoProviders ?? [],
        setFirstName,
        setLastName,
        setMobileNumber,
        setGender,
        setBirthday,
        durationInMinutes,
        services: services ?? [],
        sendOtp,
        resendOtp,
        verifyOtp,
        request,
        confirmRequest,
        reset,
        authenticatedPatientId
      }}
    >
      {children}
    </ScheduleFlowContext.Provider>
  );
};

export const useScheduleFlow = () => {
  const context = useContext(ScheduleFlowContext);
  if (context === undefined) {
    throw new Error(
      'useScheduleFlow must be used within a ScheduleFlowProvider'
    );
  }
  return context;
};
